import TCClient from '../../client';
import { Role, User } from '../../types';

/**
 * Generic helper to filter fulfilled promises from Promise.allSettled results
 * @param result - A promise settled result
 * @returns True if the promise was fulfilled
 */
export function isFulfilledResult<T>(
  result: PromiseSettledResult<T>
): result is PromiseFulfilledResult<T> {
  return result.status === 'fulfilled';
}

/**
 * Checks if a user has auto-forward configured for a given organization
 * @param user - The user object with profile information
 * @param organizationId - The organization ID to check
 * @returns true if the user has auto-forward entities or receivers configured
 */
export function hasAutoForwardConfigured(
  user: User | undefined,
  organizationId: string | undefined
): boolean {
  if (!user || !organizationId) {
    return false;
  }

  const profileByOrg = user.profileByOrganizationId?.[organizationId];
  return (
    (profileByOrg?.dndAutoForwardEntities?.length ?? 0) > 0 ||
    (profileByOrg?.autoForwardReceiverIds?.length ?? 0) > 0
  );
}

export type ForwardedEntity = {
  token: string;
  displayName: string;
  entityType: string;
};

/**
 * Processes nested forward entities recursively
 * @param nestedForwardEntities - Array of nested forward entities to process
 * @param organizationId - The organization ID
 * @returns Processed forward entities or undefined
 */
async function processNestedForwardEntities(
  nestedForwardEntities: ForwardedEntity[],
  organizationId: string
): Promise<ForwardedEntity[] | undefined> {
  if (nestedForwardEntities.length === 0) {
    return undefined;
  }

  if (nestedForwardEntities.length === 1) {
    const nestedResults = await fetchLastAutoForwardDataForChildren(
      nestedForwardEntities,
      organizationId
    );

    if (nestedResults) {
      const fulfilledValues = nestedResults
        .filter(isFulfilledResult)
        .map((r) => r.value)
        .flat()
        .filter((v): v is ForwardedEntity => !!v);

      return fulfilledValues;
    }
  } else {
    // Multiple nested entities - return them as-is
    return nestedForwardEntities.map((entity: ForwardedEntity): ForwardedEntity => {
      return {
        displayName: entity.displayName,
        token: entity.token,
        entityType: entity.entityType,
      };
    });
  }

  return undefined;
}

/**
 * Fetches the last auto-forward data for children entities
 * @param dndAutoForwardEntities - The array of auto-forward entities
 * @param organizationId - The organization ID
 * @returns An array of promise settled results
 */
export async function fetchLastAutoForwardDataForChildren(
  dndAutoForwardEntities: ForwardedEntity[],
  organizationId: string
): Promise<PromiseSettledResult<ForwardedEntity | ForwardedEntity[] | undefined>[]> {
  const processDndAutoForwardEntities = async (
    receiver: ForwardedEntity
  ): Promise<ForwardedEntity | ForwardedEntity[] | undefined> => {
    if (receiver.entityType === 'team') {
      return {
        token: receiver.token,
        entityType: receiver.entityType,
        displayName: receiver.displayName,
      };
    }

    const user = await TCClient.users.find(receiver.token, {
      bypassCache: true,
      organizationId,
    });
    const userProfile = user.profileByOrganizationId?.[organizationId];
    const nestedForwardEntities =
      userProfile?.dndAutoForwardEntities.map((entity: ForwardedEntity) => {
        return {
          token: entity.token,
          displayName: entity.displayName,
          entityType: entity.entityType,
        };
      }) || [];

    const processedNested = await processNestedForwardEntities(
      nestedForwardEntities,
      organizationId
    );
    if (processedNested && processedNested.length > 0) {
      return processedNested;
    }

    // No nested forwarding - return the receiver entity
    const token = receiver.token || user.id;
    const displayName = receiver.displayName || user.displayName;
    const entityType = receiver.entityType || user.$entityType;

    return { token, displayName, entityType };
  };

  const results = await Promise.allSettled(
    dndAutoForwardEntities.map(processDndAutoForwardEntities)
  );

  return results;
}

/**
 * Fetches the last auto-forward data for a given entity
 * @param entity - The entity to fetch the last auto-forward data for
 * @param organizationId - The organization ID
 * @returns An array of promise settled results
 */
export async function fetchLastAutoForwardData(entity: User, organizationId: string) {
  try {
    const user = await TCClient.users.find(entity.id, {
      bypassCache: true,
      organizationId,
    });
    const dndAutoForwardEntities = user.profileByOrganizationId?.[organizationId]
      ?.dndAutoForwardEntities as unknown as ForwardedEntity[];

    if (!dndAutoForwardEntities || !Array.isArray(dndAutoForwardEntities)) {
      return [];
    }

    const results = await Promise.allSettled(
      dndAutoForwardEntities.map(async (entity) => {
        if (entity.entityType === 'team' || entity.entityType === 'role') {
          return {
            token: entity.token,
            entityType: entity.entityType,
            displayName: entity.displayName,
          };
        } else if (entity.entityType === 'account') {
          const promiseResults = await fetchLastAutoForwardDataForChildren(
            [entity],
            organizationId
          );
          return promiseResults?.filter(isFulfilledResult).map((result) => result.value)[0];
        }
        return undefined;
      })
    );

    return results
      .filter(isFulfilledResult)
      .map((result) => result.value)
      .flat();
  } catch (error) {
    console.error(`Failed to fetch user ${entity.id}:`, error);
    return [];
  }
}

/**
 * Fetches the last auto-forward data for a role by fetching its owner user
 * @param role - The role entity (must have memberIds, first element is the owner)
 * @param organizationId - The organization ID
 * @returns An array of forwarded entities or empty array if fetch fails
 */
export async function fetchAutoForwardDataForRole(
  role: Role,
  organizationId: string
): Promise<(ForwardedEntity | undefined)[]> {
  try {
    const ownerId = role.memberIds?.[0];

    if (!ownerId) {
      return [];
    }

    const ownerUser = await TCClient.users.find(ownerId, {
      bypassCache: true,
      organizationId,
    });

    return fetchLastAutoForwardData(ownerUser, organizationId);
  } catch (error) {
    console.error(`Failed to fetch auto-forward data for role ${role.id}:`, error);
    return [];
  }
}

/**
 * Fetches team members with their auto-forward data
 * @param teamToken - The token of the team to fetch members for
 * @param organizationId - The organization ID
 * @returns Object containing members array, their auto-forward data, and role flags, or null if fetch fails
 */
export async function fetchTeamMembers(
  teamToken: string,
  organizationId: string
): Promise<{
  members: User[];
  memberIsRole: boolean[];
} | null> {
  try {
    const team = await TCClient.teams.find(teamToken, {
      organizationId,
      bypassCache: true,
    });

    const teamMemberObjects = team.members;
    if (!teamMemberObjects || teamMemberObjects.length === 0) {
      return null;
    }

    const members: User[] = [];
    const memberIsRole: boolean[] = [];

    for (const memberObj of teamMemberObjects) {
      if (memberObj.isRoleBot || memberObj.botRole) {
        members.push(memberObj);
        memberIsRole.push(true);
      } else {
        members.push(memberObj as User);
        memberIsRole.push(false);
      }
    }

    return { members, memberIsRole };
  } catch (error) {
    console.error(`Failed to fetch team members with auto-forward ${teamToken}:`, error);
    return null;
  }
}
